import { Suspense, useEffect } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { Icon, Spinner } from '../ui/index.js';
import { useAuth, roleHome } from '../lib/auth.js';
import { NotificationsProvider } from '../lib/notifications.js';
import { AccountMenu } from './AccountMenu.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { HatSwitcher } from './HatSwitcher.js';
import { hatForPath, hatHome } from './hats.js';
import { Logo } from './Logo.js';
import { NotificationsBell } from './NotificationsBell.js';
import { OfflineBanner } from './OfflineBanner.js';
import { navForHat, navForRole } from './nav.js';

/**
 * The signed-in layout: a responsive top nav (collapsing to a bottom tab bar on
 * phones), the notifications bell, the account menu, an offline banner, and an
 * error boundary around the routed content. Feature routes render into the
 * <Outlet/>.
 */
export function AppShell() {
  const { me, hat, hats, setHat } = useAuth();
  const location = useLocation();

  // Keep the active hat in step with the route so a deep link (or a link inside
  // a surface) lands in the right hat and the switcher highlights it. Only sync
  // to a hat the account actually has.
  useEffect(() => {
    const routeHat = hatForPath(location.pathname);
    if (routeHat && routeHat !== hat && hats.includes(routeHat)) {
      setHat(routeHat);
    }
  }, [location.pathname, hat, hats, setHat]);

  if (!me) return null;
  // The nav is scoped to the active hat so switching re-scopes the whole shell;
  // fall back to the role nav before a hat resolves.
  const nav = hat ? navForHat(hat) : navForRole(me.role);

  // The builder editor screen (a deep /build path) runs edge to edge; the rest
  // of the app uses the padded, width-capped container.
  const bleed = /^\/build\/[^/]+/.test(location.pathname);

  return (
    <NotificationsProvider>
      <div className="pt-shell">
        <OfflineBanner />

        <header className="pt-topnav">
          <div className="pt-topnav__inner">
            <Logo to={hat ? hatHome(hat) : roleHome(me.role)} />
            <HatSwitcher />
            <nav className="pt-nav-links" aria-label="Primary">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    ['pt-navlink', isActive ? 'pt-navlink--active' : ''].filter(Boolean).join(' ')
                  }
                >
                  <Icon name={item.icon} size={18} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <div className="pt-topnav__spacer" />
            <div className="pt-topnav__actions">
              <NotificationsBell />
              <AccountMenu />
            </div>
          </div>
        </header>

        <main className={['pt-shell__main', bleed ? 'pt-shell__main--bleed' : ''].filter(Boolean).join(' ')}>
          <ErrorBoundary>
            <Suspense
              fallback={
                <div
                  style={{ display: 'flex', justifyContent: 'center', padding: 'var(--pt-space-7)' }}
                >
                  <Spinner size={30} label="Loading" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>

        <nav className="pt-tabbar" aria-label="Primary">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                ['pt-tabbar__item', isActive ? 'pt-tabbar__item--active' : ''].filter(Boolean).join(' ')
              }
            >
              <Icon name={item.icon} size={22} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </NotificationsProvider>
  );
}
